import { useContext } from 'react'
import { generatePath } from 'react-router-dom'
import { CompanyTag } from '@/components/ui/badge'
import { GridCellProps, GridColumnProps } from '@progress/kendo-react-grid'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { TransactionKey } from 'api/Transaction/Types/Transaction'
import { Link } from 'components/Link'
import { generateClassNameForReverseType } from 'helpers/transactions'
import { AuthSessionContext } from 'utils/AuthContext'
import { formatLocalDateUTC } from 'utils/date'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { hasPermission } from 'utils/permissionsValidator'
import { getCreditCreditOrFirstOneWallet } from 'utils/wallet'

import './Create/transaction.scss'

export interface GridColumn extends GridColumnProps {
  show: boolean
  width?: number
}

const ReferenceCell = (props: GridCellProps): JSX.Element => {
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  if (!hasPermission(permissions, PermissionsEnum.CanViewWalletTransactions)) {
    return (
      <td className={generateClassNameForReverseType(props.dataItem)}>
        {props.dataItem.referenceId}
      </td>
    )
  }

  return (
    <td
      className={generateClassNameForReverseType(props.dataItem)}
      style={{ color: 'var(--link)', textDecoration: 'underline' }}
    >
      <Link
        to={{
          pathname: generatePath('/transactions/:id', {
            id: props.dataItem.id,
          }),
        }}
      >
        {props.dataItem.referenceId}
      </Link>
    </td>
  )
}

const walletsTransactionColumns: GridColumn[] = [
  {
    title: 'Reference',
    field: TransactionKey.reference,
    cell: ReferenceCell,
    show: true,
    className: 'transaction-columns__content',
    filter: 'text',
  },
  {
    title: 'Client',
    field: TransactionKey.walletTransactionWalletClient,
    cell: (props: GridCellProps): JSX.Element => {
      const walletTransactions = props.dataItem.walletTransactions
      if (isEmpty(walletTransactions)) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      const walletCreditTransaction = getCreditCreditOrFirstOneWallet(walletTransactions, 'Credit')
      if (!walletCreditTransaction) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      const client = walletCreditTransaction?.wallet?.client
      if (!client) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      return (
        <td
          className={generateClassNameForReverseType(props.dataItem)}
          style={{ color: 'var(--link)', textDecoration: 'underline' }}
        >
          <Link
            to={{
              pathname: generatePath(links.recordsClients.detailsUrl as string, {
                id: client.id,
                slug: 'details',
              }),
            }}
            state={{ redirectTo: links.transactionWallet.url }}
          >
            {client.name}
          </Link>
        </td>
      )
    },
    show: true,
    filter: 'text',
  },
  {
    title: 'Source',
    show: true,
    field: 'Source',
    children: [
      {
        title: 'Wallet',
        field: TransactionKey.walletDebitTransactionWallet,
        cell: (props: GridCellProps): JSX.Element => {
          const walletTransactions = props.dataItem.walletTransactions
          if (isEmpty(walletTransactions)) {
            return <td className={generateClassNameForReverseType(props.dataItem)} />
          }

          const walletDebitTransaction = getCreditCreditOrFirstOneWallet(
            walletTransactions,
            'Debit'
          )
          const wallet = walletDebitTransaction?.wallet
          if (!wallet) {
            return <td className={generateClassNameForReverseType(props.dataItem)} />
          }

          return (
            <td
              className={generateClassNameForReverseType(props.dataItem)}
              style={{ color: 'var(--link)', textDecoration: 'underline' }}
            >
              <Link
                to={{
                  pathname: generatePath(links.recordsWallets.detailsUrl as string, {
                    id: wallet.id,
                    slug: 'details',
                  }),
                }}
                state={{ redirectTo: links.transactionWallet.url }}
              >
                {wallet.name}
              </Link>
            </td>
          )
        },
        resizable: true,
        filter: 'text',
      },
      {
        title: 'Amount',
        field: TransactionKey.walletDebitTransactionAmount,
        cell: (props: GridCellProps): JSX.Element => {
          const walletTransactions = props.dataItem.walletTransactions
          if (isEmpty(walletTransactions)) {
            return <td className={generateClassNameForReverseType(props.dataItem, 'number')} />
          }

          const walletDebitTransaction = getCreditCreditOrFirstOneWallet(
            walletTransactions,
            'Debit'
          )
          const amount = walletDebitTransaction?.amount?.toFixed(2)
          return (
            <td className={generateClassNameForReverseType(props.dataItem, 'number')}>{amount}</td>
          )
        },
        resizable: true,
        filter: 'text',
      },
      {
        title: 'Currency',
        field: TransactionKey.walletDebitTransactionWalletCurrency,
        cell: (props: GridCellProps): JSX.Element => {
          const walletTransactions = props.dataItem.walletTransactions
          if (isEmpty(walletTransactions)) {
            return <td className={generateClassNameForReverseType(props.dataItem)} />
          }

          const walletDebitTransaction = getCreditCreditOrFirstOneWallet(
            walletTransactions,
            'Debit'
          )
          const currencyId = walletDebitTransaction?.wallet?.currency?.id
          if (isEmpty(currencyId)) {
            return <td className={generateClassNameForReverseType(props.dataItem)} />
          }

          return <td className={generateClassNameForReverseType(props.dataItem)}>{currencyId}</td>
        },
        resizable: true,
        filter: 'text',
      },
      {
        title: 'Product',
        field: 'products',
        cell: (props: GridCellProps): JSX.Element => {
          const walletTransactions = props.dataItem.walletTransactions
          if (isEmpty(walletTransactions)) {
            return <td className={generateClassNameForReverseType(props.dataItem)} />
          }
          const walletDebitTransaction = getCreditCreditOrFirstOneWallet(
            walletTransactions,
            'Debit'
          )
          const wallet = walletDebitTransaction?.wallet
          if (!wallet) {
            return <td className={generateClassNameForReverseType(props.dataItem)} />
          }

          return <td>{wallet.walletType.name}</td>
        },
        resizable: true,
        filter: 'text',
      },
    ],
  },
  {
    title: 'Destination',
    show: true,
    field: 'Destination',
    children: [
      {
        title: 'Wallet',
        field: TransactionKey.walletCreditTransactionWallet,
        cell: (props: GridCellProps): JSX.Element => {
          const walletTransactions = props.dataItem.walletTransactions
          if (isEmpty(walletTransactions)) {
            return <td className={generateClassNameForReverseType(props.dataItem)} />
          }

          const walletCreditTransaction = getCreditCreditOrFirstOneWallet(
            walletTransactions,
            'Credit'
          )
          const wallet = walletCreditTransaction?.wallet
          if (!wallet) {
            return <td className={generateClassNameForReverseType(props.dataItem)} />
          }

          return (
            <td
              className={generateClassNameForReverseType(props.dataItem)}
              style={{ color: 'var(--link)', textDecoration: 'underline' }}
            >
              <Link
                to={{
                  pathname: generatePath(links.recordsWallets.detailsUrl as string, {
                    id: wallet.id,
                    slug: 'details',
                  }),
                }}
                state={{ redirectTo: links.transactionWallet.url }}
              >
                {wallet.name}
              </Link>
            </td>
          )
        },
        resizable: true,
        filter: 'text',
      },
      {
        title: 'Amount',
        field: TransactionKey.walletCreditTransactionAmount,
        cell: (props: GridCellProps): JSX.Element => {
          const walletTransactions = props.dataItem.walletTransactions
          if (isEmpty(walletTransactions)) {
            return <td className={generateClassNameForReverseType(props.dataItem, 'number')} />
          }

          const walletCreditTransaction = getCreditCreditOrFirstOneWallet(
            walletTransactions,
            'Credit'
          )
          const amount = walletCreditTransaction?.amount?.toFixed(2)

          return (
            <td className={generateClassNameForReverseType(props.dataItem, 'number')}>{amount}</td>
          )
        },
        resizable: true,
        filter: 'text',
      },
      {
        title: 'Currency',
        field: TransactionKey.walletCreditTransactionWalletCurrency,
        cell: (props: GridCellProps): JSX.Element => {
          const walletTransactions = props.dataItem.walletTransactions
          if (isEmpty(walletTransactions)) {
            return <td className={generateClassNameForReverseType(props.dataItem)} />
          }

          const walletCreditTransaction = getCreditCreditOrFirstOneWallet(
            walletTransactions,
            'Credit'
          )
          if (isEmpty(walletCreditTransaction)) {
            return <td className={generateClassNameForReverseType(props.dataItem)} />
          }

          const currencyId = walletCreditTransaction?.wallet?.currency?.id
          if (isEmpty(currencyId)) {
            return <td className={generateClassNameForReverseType(props.dataItem)} />
          }

          return <td className={generateClassNameForReverseType(props.dataItem)}>{currencyId}</td>
        },
        resizable: true,
        filter: 'text',
      },
      {
        title: 'Wallet Type',
        field: 'Wallet Type',
        cell: (props: GridCellProps): JSX.Element => {
          const walletTransactions = props.dataItem.walletTransactions
          if (isEmpty(walletTransactions)) {
            return <td className={generateClassNameForReverseType(props.dataItem)} />
          }
          const walletDebitTransaction = getCreditCreditOrFirstOneWallet(
            walletTransactions,
            'Credit'
          )
          const wallet = walletDebitTransaction?.wallet
          if (!wallet) {
            return <td className={generateClassNameForReverseType(props.dataItem)} />
          }

          return <td>{wallet.walletType.name}</td>
        },
        resizable: true,
        filter: 'text',
      },
    ],
  },
  {
    title: 'Rate',
    field: TransactionKey.walletCreditTransactionCurrencyRate,
    cell: (props: GridCellProps): JSX.Element => {
      const walletTransactions = props.dataItem.walletTransactions
      if (isEmpty(walletTransactions)) {
        return <td className={generateClassNameForReverseType(props.dataItem, 'number')} />
      }

      const walletCreditTransaction = getCreditCreditOrFirstOneWallet(
        props.dataItem.walletTransactions,
        'Debit'
      )
      const currencyRate = walletCreditTransaction?.currencyRate?.toFixed(6)

      return (
        <td className={generateClassNameForReverseType(props.dataItem, 'number')}>
          {currencyRate}
        </td>
      )
    },
    resizable: true,
    show: true,
    filter: 'text',
  },
  {
    title: 'Status',
    field: TransactionKey.state,
    cell: (props: GridCellProps): JSX.Element => {
      const status = props.dataItem.state
      if (isEmpty(status)) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      return <td className={generateClassNameForReverseType(props.dataItem)}>{status.name}</td>
    },
    show: true,
    filter: 'text',
  },
  {
    title: 'Entity',
    field: TransactionKey.walletTransactionWalletCompany,
    cell: (props: GridCellProps): JSX.Element => {
      const walletTransactions = props.dataItem.walletTransactions
      if (isEmpty(walletTransactions)) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      const walletCreditTransaction = getCreditCreditOrFirstOneWallet(walletTransactions, 'Credit')
      const company = walletCreditTransaction?.wallet?.company
      if (!company) {
        return <td className={generateClassNameForReverseType(props.dataItem)} />
      }

      return (
        <td className={generateClassNameForReverseType(props.dataItem)}>
          <CompanyTag id={company.id}>{company.name}</CompanyTag>
        </td>
      )
    },
    resizable: true,
    show: true,
    filter: 'text',
    width: 80,
    minResizableWidth: 80,
  },
  {
    title: 'Date Created',
    field: TransactionKey.createdDate,
    cell: (props: GridCellProps): JSX.Element => {
      return (
        <td className={generateClassNameForReverseType(props.dataItem, 'date')}>
          {formatLocalDateUTC(props.dataItem.createdDate, true)}
        </td>
      )
    },
    show: true,
    resizable: true,
    filter: 'text',
  },
  {
    title: 'Date Updated',
    field: TransactionKey.updatedDate,
    cell: (props: GridCellProps): JSX.Element => {
      return (
        <td className={generateClassNameForReverseType(props.dataItem, 'date')}>
          {formatLocalDateUTC(props.dataItem.updatedDate, true)}
        </td>
      )
    },
    show: true,
    resizable: true,
    filter: 'text',
  },
]

export default walletsTransactionColumns
